import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  CalendarDatePipe,
  CalendarEvent,
  CalendarEventTitlePipe,
  CalendarMonthViewBeforeRenderEvent,
  CalendarMonthViewComponent,
  CalendarNextViewDirective,
  CalendarPreviousViewDirective,
  CalendarTodayDirective,
  CalendarTooltipDirective,
  CalendarView,
  DAYS_OF_WEEK,
} from 'angular-calendar';
import dayjs from 'dayjs';
import { Subject } from 'rxjs';
import { Contract } from 'src/app/models/contract.model';
import { ApiService } from 'src/app/services/api.service';
import { UtilsService } from 'src/app/services/utils.service';
import { AssignNewGuardDialogComponent } from './assign-new-guard-dialog/assign-new-guard-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PermissionsService } from 'src/app/services/permissions.service';
import { MatIconButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { CompanyProductPageIdentityComponent } from '../company-product-page-identity/company-product-page-identity.component';

@Component({
  selector: 'app-company-product-guardian-scheduled-weeks',
  templateUrl: './company-product-guardian-scheduled-weeks.component.html',
  styleUrls: ['./company-product-guardian-scheduled-weeks.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CalendarPreviousViewDirective,
    CalendarNextViewDirective,
    CalendarTodayDirective,
    CalendarDatePipe,
    CalendarEventTitlePipe,
    CalendarTooltipDirective,
    CalendarMonthViewComponent,
    MatIconButton,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    NgClass,
    MatTooltip,
    CompanyProductPageIdentityComponent,
  ],
})
export class CompanyProductGuardianScheduledWeeksComponent implements OnInit {
  protected activatedRoute = inject(ActivatedRoute);
  protected utils = inject(UtilsService);
  private api = inject(ApiService);
  private dialog = inject(MatDialog);
  private snack = inject(MatSnackBar);
  private permissions = inject(PermissionsService);

  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  now: Date = new Date();
  filterForm: FormGroup;
  events: CalendarEvent[] = [];
  viewDate: Date = new Date();
  locale: string = 'es';
  weekStartsOn: number = DAYS_OF_WEEK.MONDAY;
  weekendDays: number[] = [DAYS_OF_WEEK.SATURDAY, DAYS_OF_WEEK.SUNDAY];
  refresh = new Subject<void>();
  view: CalendarView = CalendarView.Month;
  private guardian_contracts: Contract[] = null as any;
  isCompanyProductManagerOrProductManager: boolean = false;

  private company_product_id: string = null as any;

  ngOnInit(): void {
    this.company_product_id = this.activatedRoute.snapshot.parent?.parent?.paramMap.get('id') as string;
    this.onViewDateChange();
  }

  onViewDateChange() {
    this.events = [];
    this.fetchVacations();
    this.fetchHolidays();
    this.fetchGuardianContracts();
    this.fetchGuardianScheduledWeeks();
    this.initIsPMorPO();
  }

  async beforeMonthViewRender(renderEvent: CalendarMonthViewBeforeRenderEvent) {
    const bgGreyCssClass: string = 'bg-grey';

    while (this.guardian_contracts == null) {
      await this.utils.sleep(100);
    }

    renderEvent.body.forEach(day => {
      let dayCssClass: string = '';
      const today: dayjs.Dayjs = dayjs(day.date);

      // bg-grey class
      let availableGuardian: boolean = false;
      if (this.guardian_contracts.length == 0) dayCssClass += bgGreyCssClass + ' ';
      else {
        for (const contract of this.guardian_contracts) {
          const startsAt: dayjs.Dayjs = dayjs(contract.starts_at);
          if (contract.ends_at == null) {
            availableGuardian = today.isSameOrAfter(startsAt, 'day');
          } else if (contract.ends_at != null) {
            const endsAt: dayjs.Dayjs = dayjs(contract.ends_at);
            availableGuardian = today.isSameOrAfter(startsAt, 'day') && today.isSameOrBefore(endsAt);
          }

          if (availableGuardian) break;
        }

        if (!availableGuardian) dayCssClass += bgGreyCssClass + ' ';
      }

      // clickable
      if (availableGuardian && today.isAfter(dayjs()) && today.isoWeekday() === 1) dayCssClass += 'clickable ';

      day.cssClass = dayCssClass;
    });
  }

  onDayClicked(event: { day: any; sourceEvent: MouseEvent | KeyboardEvent }) {
    const day: dayjs.Dayjs = dayjs(event.day.date);

    if (
      this.isCompanyProductManagerOrProductManager &&
      ['BUTTON', 'MAT-ICON'].indexOf((event.sourceEvent.target! as any).tagName as string) == -1
    ) {
      if ((event.sourceEvent.target as any)!.closest('mwl-calendar-month-cell').classList.contains('clickable')) {
        this.assignNewGuard(day.toDate());
      } else if (day.isoWeekday() !== 1) {
        this.snack.open('Deberías seleccionar un lunes.', 'OK');
      } else {
        this.snack.open('Este día no es válido para asignar guardias. Debes seleccionar un día en azul.', 'OK');
      }
    }
  }

  assignNewGuard(date: Date) {
    const dialogRef = this.dialog.open(AssignNewGuardDialogComponent, {
      width: '400px',
      data: {
        company_product_id: this.company_product_id,
        from: dayjs(date).startOf('week'),
        to: dayjs(date).endOf('week'),
      },
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        this.onViewDateChange();
      }
    });
  }

  unassignGuard(day: { events: any[] }) {
    if (
      day.events.length &&
      confirm('¿Estás seguro que quieres desasignar las guardias de esta semana? Se notificará a las personas implicadas.')
    ) {
      for (const event of day.events) {
        if (event.meta.type === 'guardian') {
          // ha de ser eliminat
          this.api.deleteGuardianScheduledWeek(event.meta.id).subscribe(data => {
            this.onViewDateChange();
          });
        }
      }
    }
  }

  private fetchVacations() {
    const from = dayjs(this.viewDate).startOf('month').startOf('week').toDate();
    const to = dayjs(this.viewDate).endOf('month').endOf('week').toDate();
    this.api.getCompanyProductVacations(this.company_product_id, from, to).subscribe(data => {
      this.events.push(
        ...data.map(item => {
          return {
            title: item.user!.name + ' ' + item.user!.surnames,
            color: {
              primary: this.stringToColour(item.user!.name + ' ' + item.user!.surnames),
              secondary: '#FAE3E3',
            },
            start: item.from_date,
            end: item.to_date,
            allDay: item.all_day,
            meta: {
              id: item.id,
              type: 'vacation',
            },
          };
        })
      );
      this.refresh.next();
    });
  }

  private stringToColour(str: string) {
    let hash = 0;
    for (var i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let colour = '#';
    for (var i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      colour += ('00' + value.toString(16)).substr(-2);
    }
    return colour;
  }

  private fetchHolidays() {
    const from = dayjs(this.viewDate).startOf('month').startOf('week').toDate();
    const to = dayjs(this.viewDate).endOf('month').endOf('week').toDate();
    this.api.getCompanyProductHolidays(this.company_product_id, from, to).subscribe(data => {
      const grouped: any[] = [];

      data.forEach(item => {
        const index = grouped.findIndex((i: any) => i.slug == item.slug);
        if (index == -1) {
          grouped.push({
            name: item.name,
            slug: item.slug,
            users: item.username + (item.surnames ? ' ' + item.surnames : ''),
            from_date: item.date,
            to_date: item.date,
            all_day: true,
          });
        } else {
          grouped[index].users += ', ' + item.username + (item.surnames ? ' ' + item.surnames : '');
        }
      });

      this.events.push(
        ...grouped.map(item => {
          return {
            title: item.name + ' (' + item.users + ')',
            color: {
              primary: '#FFD700',
              secondary: '#FAE3E3',
            },
            start: item.from_date,
            end: item.to_date,
            allDay: true,
            meta: {
              id: item.id,
              type: 'holiday',
            },
          };
        })
      );

      this.refresh.next();
    });
  }

  private fetchGuardianContracts() {
    this.guardian_contracts = null as any;
    const from = dayjs(this.viewDate).startOf('month').startOf('week').toDate();
    const to = dayjs(this.viewDate).endOf('month').endOf('week').toDate();
    this.api.getCompanyProductGuardianContracts(this.company_product_id, from, to).subscribe(data => {
      this.guardian_contracts = data;
    });
  }

  private fetchGuardianScheduledWeeks() {
    const from = dayjs(this.viewDate).startOf('month').startOf('week');
    const to = dayjs(this.viewDate).endOf('month').endOf('week');
    const body: any = {
      company_product_id: this.company_product_id,
      from: from.format('YYYY-MM-DD'),
      to: to.format('YYYY-MM-DD'),
      with: 'user',
    };
    this.api.getGuardianScheduledWeeks(body).subscribe(data => {
      this.events.push(
        ...data.map(item => {
          return {
            title: item.user.name + ' ' + item.user.surnames,
            color: {
              primary: this.stringToColour(item.user.name + ' ' + item.user.surnames),
              secondary: '#FAE3E3',
            },
            start: item.start_week,
            end: item.to_week,
            allDay: true,
            meta: {
              id: item.id,
              type: 'guardian',
              user: item.user,
            },
          };
        })
      );
      this.refresh.next();
    });
  }

  private initIsPMorPO() {
    this.isCompanyProductManagerOrProductManager =
      this.permissions.isBisualProductionTeamInCompanyProductWithRole(this.company_product_id, 'tech-lead') ||
      this.permissions.isBisualProductionTeamInCompanyProductWithRole(this.company_product_id, 'product-manager');
  }
}
